import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import CheckboxGroup from "../../components/forms/CheckboxGroup";
import SelectInput from "../../components/forms/SelectInput";
import MultiSelectInput from "../../components/forms/MultiSelectInput";
import SliderInput from "../../components/forms/SliderInput";
import NotesField from "../../components/forms/NotesField";
import { useStore } from "../../js/state/store";
import {
  ARCHITECTURAL_FEATURES,
  BATHROOM_DETAILS,
  BONUS_ROOMS,
  CABINET_STYLES,
  CEILING_HEIGHTS,
  CEILING_TYPES,
  COUNTERTOP_TYPES,
  FLOORING_TYPES,
  KITCHEN_DETAILS,
  KITCHEN_LAYOUTS,
  WALL_TYPES,
} from "../../js/utils/checklistOptions";

export default function BuyerInterior() {
  const saved = useStore().state.buyer.current.preferences.interior || {};
  const [form, setForm] = useState({
    ceilingHeight: saved.ceilingHeight || "",
    ceilingTypes: saved.ceilingTypes || [],
    ceilingNotes: saved.ceilingNotes || "",
    wallTypes: saved.wallTypes || [],
    wallNotes: saved.wallNotes || "",
    flooring: saved.flooring || [],
    flooringNotes: saved.flooringNotes || "",
    architectural: saved.architectural || [],
    architecturalNotes: saved.architecturalNotes || "",
    kitchenSize: saved.kitchenSize || "",
    kitchenPrimaryLayout: saved.kitchenPrimaryLayout || "",
    kitchenLayout: saved.kitchenLayout || [],
    kitchenDetails: saved.kitchenDetails || saved.appliances || [],
    countertops: saved.countertops || [],
    cabinets: saved.cabinets || [],
    kitchenNotes: saved.kitchenNotes || "",
    livingRoom: saved.livingRoom || "",
    fireplace: saved.fireplace || "",
    naturalLight: saved.naturalLight || "",
    bedrooms: saved.bedrooms ?? 3,
    masterSuite: saved.masterSuite || [],
    closets: saved.closets || [],
    bathrooms: saved.bathrooms || [],
    bathroomDetails: saved.bathroomDetails || [],
    bathNotes: saved.bathNotes || "",
    bonusRooms: saved.bonusRooms || [],
    bonusNotes: saved.bonusNotes || "",
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "range" ? Number(value) : value,
    }));
  }

  return (
    <ChecklistStep
      title="Interior"
      lead="Ceilings, walls, floors, architectural details, kitchen, baths, and the extra rooms that make daily life work."
      preferenceKey="interior"
      value={form}
    >
      <section className="checklist-section">
        <h2>Ceilings</h2>
        <p className="section-help">Height is a single preference. Ceiling types can be mixed — select every look you would live with.</p>
        <SelectInput
          label="Preferred ceiling height"
          name="ceilingHeight"
          value={form.ceilingHeight}
          onChange={update}
          options={CEILING_HEIGHTS}
        />
        <MultiSelectInput
          label="Ceiling types"
          name="ceilingTypes"
          values={form.ceilingTypes}
          onChange={update}
          options={CEILING_TYPES}
        />
        <NotesField
          label="Ceiling notes for my agent"
          name="ceilingNotes"
          value={form.ceilingNotes}
          onChange={update}
          rows={3}
          placeholder="Popcorn must go, beams are a must, or volume ceilings only in the great room…"
        />
      </section>

      <section className="checklist-section">
        <h2>Walls</h2>
        <MultiSelectInput
          label="Wall types and finishes"
          name="wallTypes"
          values={form.wallTypes}
          onChange={update}
          options={WALL_TYPES}
          size={8}
        />
        <NotesField
          label="Wall notes for my agent"
          name="wallNotes"
          value={form.wallNotes}
          onChange={update}
          rows={3}
          placeholder="Accent walls you love, wallpaper you will strip, or texture you cannot live with."
        />
      </section>

      <section className="checklist-section">
        <h2>Flooring</h2>
        <MultiSelectInput
          label="Flooring types"
          name="flooring"
          values={form.flooring}
          onChange={update}
          options={FLOORING_TYPES}
        />
        <NotesField
          label="Flooring notes for my agent"
          name="flooringNotes"
          value={form.flooringNotes}
          onChange={update}
          rows={3}
          placeholder="Hardwood throughout, carpet only in bedrooms, or no tile in the kitchen…"
        />
      </section>

      <section className="checklist-section">
        <h2>Architectural Features</h2>
        <MultiSelectInput
          label="Features that matter"
          name="architectural"
          values={form.architectural}
          onChange={update}
          options={ARCHITECTURAL_FEATURES}
          size={8}
        />
        <NotesField
          label="Architectural notes for my agent"
          name="architecturalNotes"
          value={form.architecturalNotes}
          onChange={update}
          rows={3}
          placeholder="Arches, built-ins, or extra light through skylights and garden windows."
        />
      </section>

      <section className="checklist-section">
        <h2>Kitchen Details</h2>
        <ChoiceGroup
          label="Kitchen size"
          name="kitchenSize"
          value={form.kitchenSize}
          onChange={update}
          options={[
            { value: "compact", label: "Compact is fine" },
            { value: "standard", label: "Standard" },
            { value: "large", label: "Large" },
            { value: "gourmet", label: "Gourmet / chef" },
          ]}
        />
        <SelectInput
          label="Primary kitchen layout"
          name="kitchenPrimaryLayout"
          value={form.kitchenPrimaryLayout}
          onChange={update}
          options={KITCHEN_LAYOUTS}
        />
        <CheckboxGroup
          label="Other kitchen layouts you would consider"
          name="kitchenLayout"
          values={form.kitchenLayout}
          onChange={update}
          layout="grid"
          options={KITCHEN_LAYOUTS}
        />
        <MultiSelectInput
          label="Kitchen details"
          name="kitchenDetails"
          values={form.kitchenDetails}
          onChange={update}
          options={KITCHEN_DETAILS}
          size={7}
        />
        <CheckboxGroup
          label="Countertop materials"
          name="countertops"
          values={form.countertops}
          onChange={update}
          layout="grid"
          options={COUNTERTOP_TYPES}
        />
        <CheckboxGroup
          label="Cabinet styles"
          name="cabinets"
          values={form.cabinets}
          onChange={update}
          layout="grid"
          options={CABINET_STYLES}
        />
        <NotesField
          label="Kitchen notes for my agent"
          name="kitchenNotes"
          value={form.kitchenNotes}
          onChange={update}
          rows={3}
          placeholder="Work triangle, appliance brands, or a must-have pantry."
        />
      </section>

      <section className="checklist-section">
        <h2>Living Spaces</h2>
        <SelectInput
          label="Living room size"
          name="livingRoom"
          value={form.livingRoom}
          onChange={update}
          options={[
            { value: "modest", label: "Modest / cozy" },
            { value: "standard", label: "Standard" },
            { value: "great-room", label: "Great room" },
          ]}
        />
        <ChoiceGroup
          label="Fireplace type"
          name="fireplace"
          value={form.fireplace}
          onChange={update}
          options={[
            { value: "none", label: "Not needed" },
            { value: "wood", label: "Wood-burning" },
            { value: "gas", label: "Gas" },
            { value: "electric", label: "Electric" },
            { value: "any", label: "Any fireplace" },
          ]}
        />
        <ChoiceGroup
          label="Natural light"
          name="naturalLight"
          value={form.naturalLight}
          onChange={update}
          options={[
            { value: "abundant", label: "Abundant light" },
            { value: "moderate", label: "Moderate" },
            { value: "flexible", label: "Flexible" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Bathroom Details</h2>
        <SliderInput label="Bedroom count (minimum)" name="bedrooms" min={1} max={8} value={form.bedrooms} onChange={update} />
        <CheckboxGroup
          label="Primary suite requirements"
          name="masterSuite"
          values={form.masterSuite}
          onChange={update}
          layout="grid"
          options={[
            { value: "ensuite", label: "En-suite bath" },
            { value: "walk-in", label: "Walk-in closet" },
            { value: "sitting", label: "Sitting area" },
            { value: "main-floor", label: "On the main floor" },
            { value: "separated", label: "Away from other bedrooms" },
          ]}
        />
        <CheckboxGroup
          label="Closet types"
          name="closets"
          values={form.closets}
          onChange={update}
          layout="grid"
          options={[
            { value: "walk-in", label: "Walk-in closets" },
            { value: "reach-in", label: "Reach-in is fine" },
            { value: "custom", label: "Custom organizers a plus" },
            { value: "linen", label: "Linen / hall storage" },
          ]}
        />
        <MultiSelectInput
          label="Bathroom configurations"
          name="bathroomDetails"
          values={form.bathroomDetails.length ? form.bathroomDetails : form.bathrooms}
          onChange={(event) => {
            update(event);
            update({ target: { name: "bathrooms", value: event.target.value } });
          }}
          options={BATHROOM_DETAILS}
          size={7}
        />
        <NotesField
          label="Bathroom notes for my agent"
          name="bathNotes"
          value={form.bathNotes}
          onChange={update}
          rows={3}
          placeholder="Soaking tub vs. oversized shower, dual vanities, or a guest bath that must feel finished."
        />
      </section>

      <section className="checklist-section">
        <h2>Bonus Rooms</h2>
        <MultiSelectInput
          label="Extra spaces that matter"
          name="bonusRooms"
          values={form.bonusRooms}
          onChange={update}
          options={BONUS_ROOMS}
          size={7}
        />
        <NotesField
          label="Bonus-room notes for my agent"
          name="bonusNotes"
          value={form.bonusNotes}
          onChange={update}
          rows={3}
          placeholder="Office that can become a nursery, or a basement you will finish later."
        />
        <NotesField
          label="Interior notes for my agent"
          name="notes"
          value={form.notes}
          onChange={update}
          placeholder="Must-update vs. livable finishes, paint colors you refuse, or rooms you will convert."
        />
      </section>
    </ChecklistStep>
  );
}
